import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AnalystHomeButtons,
  Breadcrumbs,
  HamburgerAndTitleContainer,
  HomeButton,
  LogOutButton,
  PipeSeparator,
  SessionButtons,
  StyledHeader,
  ThemedIcon,
  ToggleWrapper,
  UserName,
} from "./header.styled";
import { useMediaQuery, useTheme } from "@mui/material";
import VectorHamburgerIcon from "assets/VectorHamburgerIcon";
import { AuthContext } from "core/contexts/auth/auth";
import { useAppDispatch, useAppSelector } from "core/store/reduxStore";
import {
  openSearch,
  openAllFilters,
  setFiltersCleared,
  setBreadCrumbs,
  setHeader,
} from "core/store/shared/header";
import MagnifierLogo from "assets/Magnifier";
import FilterIconWithoutBadge from "assets/LargeFilterIconWithoutBadge";
import CrossBtnIcon from "assets/CrossBtnIcon";
import { SidebarContext } from "core/contexts/sidebar/sidebar";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { useAppTheme } from "core/contexts/theme/theme";
import { styled } from "styled-components";
import { useRef } from "react";
import { setHeaderHeight } from "core/store/shared/header";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { setActiveModel } from "modules/chat/store/conversationList";
import { v4 as uuid4 } from "uuid";
import HomeIcon from '@mui/icons-material/Home';


interface HeaderIconClickProps {
  openDrawer: boolean;
  screenSize?: string;
}

const Header = (): JSX.Element => {
  const headerRef = useRef<HTMLDivElement>(null); // ✅ create the ref
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { amsUser } = useContext(AuthContext);
  const { signOut } = useContext(AuthContext);
  const headerState = useAppSelector((state) => state.header);
  const [crumbs, setCrumbs] = useState<any[]>([]);
  const theme = useTheme();

  const { setIsDrawerOpen } = useContext(SidebarContext);

  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        dispatch(setHeaderHeight(height));
      }
    };

    updateHeight();

    window.addEventListener("resize", updateHeight);

    return () => {
      window.removeEventListener("resize", updateHeight);
    };
  }, [dispatch, headerRef]);
  
  const handleHeaderIconClick = ({
    openDrawer,
    screenSize,
  }: HeaderIconClickProps) => {
    setIsDrawerOpen(openDrawer);
  };

  const isSmallerThan = useMediaQuery(theme.breakpoints.down("sm"));
  const { apptheme, toggleTheme } = useAppTheme();
  const isLight = apptheme.name === "light-theme";

  useEffect(() => {
    if (isSmallerThan) {
      // Close the drawer when the screen is a laptop or larger
    } else {
      setIsDrawerOpen(false);
    }
  }, [isSmallerThan]);

  // console.log(`headerState.crumbs: ${JSON.stringify(headerState.crumbs)}`);

  const location = useLocation();

  useEffect(() => {
    // Only update crumbs when location changes
    if (location.pathname === '/console') {
      dispatch(setBreadCrumbs([{ displayText: "Models", path: "/console" }]));
      dispatch(setHeader("mySecurity AI Engine"));
    }
    // Don't include headerState.crumbs in dependency array
  }, [location.pathname, dispatch]);

  // Separate useEffect for updating local crumbs state
  useEffect(() => {
    if(headerState !== undefined && headerState.crumbs !== undefined) {
      setCrumbs(headerState.crumbs)
    }
  }, [headerState.crumbs])


  // Update HomeButton click handler
  const handleHomeClick = () => {
    navigate("/console");
    // These dispatches will trigger the location change effect above
  };

  /**
   * This checks to see if there is a translation of this route
   * If a translation is found it will return this value
   * @param route the value of the sidebar displayText
   * @returns a translated route or the original text
   */
  const getTranslatedName = (route) => {
    const response = t(`SIDEBAR.${route}`);
    if(response.includes("SIDEBAR.")) {
      return(route)
    } else {
      return(response)
    }
  }

  return (
    <StyledHeader ref={headerRef}>
      {isSmallerThan && (
        <HamburgerAndTitleContainer>
          <div className="header-icon">
            <div
              className="hamburger-icon me-2"
              onClick={() => handleHeaderIconClick({ openDrawer: true })}
            >
              <VectorHamburgerIcon />
            </div>
            <div className="toggle-icon">
              <ToggleWrapper>
                <div className="toggle-container">
                  <button onClick={toggleTheme}>
                    {isLight ? (
                      <ThemedIcon
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                      </ThemedIcon>
                    ) : (
                      <ThemedIcon
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="5" />
                        <path d="M12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
                      </ThemedIcon>
                    )}
                  </button>
                </div>
              </ToggleWrapper>
            </div>
          </div>
        </HamburgerAndTitleContainer>
      )}

      <Breadcrumbs className="p-2">
        {crumbs.length > 0 && (
          <nav className="text-xs text-white mt-1" aria-label="breadcrumb">
            <ul className="flex items-center space-x-2 m-1 p-1">
              {crumbs.map((route, index) => (
                <span key={uuid4()}>
                  {(route !== null) && 
                    <li className="flex items-center">
                      <Link 
                        to={route.path} 
                        onClick={() => {
                          if (route.path) {
                            dispatch(setBreadCrumbs(crumbs.slice(0, index + 1)));
                          }
                          dispatch(setActiveModel(route.displayText));
                        }}
                        className={`${index === crumbs.length-1 ? 'font-bold' : ''} px-1`}
                      >
                        {getTranslatedName(route.displayText)}
                      </Link>
                      {index !== crumbs.length - 1 && (
                        <span className="flex items-center">
                          <KeyboardArrowRight />
                        </span>
                      )}
                    </li>
                  }
                </span>
              ))}
            </ul>
          </nav>
        )}
      </Breadcrumbs>

      <SessionButtons>
        
        <UserName>
          <h3>{amsUser?.username || ""}</h3>
        </UserName>
        
        <HomeButton onClick={handleHomeClick}>
          <HomeIcon />
        </HomeButton> 

        <ToggleWrapper>
          <div className="toggle-container">
            <button onClick={toggleTheme}>
              {isLight ? (
                <ThemedIcon
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="5" />
                  <path d="M12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
                </ThemedIcon>
              ) : (
                <ThemedIcon
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                </ThemedIcon>
              )}
            </button>
          </div>
        </ToggleWrapper>

        <PipeSeparator />

        <LogOutButton
          data-testid="signout-btn"
          onClick={() => {
            signOut();
          }}
        >
          <p className="font-semibold">{t("LOGIN.LOGOUT")}</p>
        </LogOutButton>
      </SessionButtons>

      {headerState.title === t("SIDEBAR.FLYWHEEL_INCIDENTS") ||
      headerState.title === t("SIDEBAR.AC2_CASES_LONG") ? (
        <AnalystHomeButtons>
          <div onClick={() => dispatch(openSearch(true))}>
            <MagnifierLogo />
          </div>
          <div onClick={() => dispatch(openAllFilters(true))}>
            <FilterIconWithoutBadge />
          </div>
          {headerState.filtersApplied === true ? (
            <div onClick={() => dispatch(setFiltersCleared(true))}>
              <CrossBtnIcon />
            </div>
          ) : null}
        </AnalystHomeButtons>
      ) : null}
    </StyledHeader>
  );
};

export default Header;